import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function PlatformPermissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/permissions/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setPermission(json.permission);
    } catch (e) {
      setError(e.message);
      setPermission(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/permissions/${id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      navigate('/platform/permissions');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !permission) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!permission) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  const isSystem = permission.is_system_permission || permission.is_system;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{permission.permission_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            <code>{permission.permission_code}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/permissions')}>
            Back to list
          </Button>
          {isSuper && (
            <>
              <Button variant="contained" onClick={() => navigate(`/platform/permissions/${id}/edit`)}>
                Edit
              </Button>
              {!isSystem && (
                <Button color="error" variant="outlined" onClick={() => setConfirmOpen(true)}>
                  Delete
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={permission.status} color={statusColor(permission.status)} />
          {isSystem ? <Chip label="System" /> : <Chip label="Custom" variant="outlined" />}
        </Box>
        <Grid container spacing={2}>
          {row('Module', permission.module_name)}
          {row('Action', permission.action_name)}
          {row('Description', permission.description)}
          {row('Role assignments', String(permission.role_assignment_count ?? '—'))}
          {row('Created', permission.created_at ? String(permission.created_at) : '—')}
          {row('Updated', permission.updated_at ? String(permission.updated_at) : '—')}
        </Grid>
        {isSystem && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              System permissions cannot be deleted or deactivated. You may adjust display name and description only.
            </Typography>
          </>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle>Delete permission?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Only allowed when no roles reference this permission. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" onClick={doDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
